import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { QueryKey, useQuery, useQueryClient } from '@tanstack/react-query'
import { doc, getDoc, getDocs, onSnapshot, QueryConstraint } from 'firebase/firestore'
import { createFirestoreQuery, mapSnapshotToDocuments, writeDocument, updateDocument } from '../services/firestore.service'
import { createSkipNextRealtimeAtom } from '../stores/firestoreAtoms'
import { useAtom } from 'jotai'
import { FirestoreData } from '../types/firestore.types'
import { removeDataUpdater, Updater } from '../utils/query.utils'
import { getDb } from '../services/firebase.service'

/**
 * Custom hook to submit a document to a specified Firestore collection.
 *
 * @template TInput - The type of the input document data.
 * @template TOutput - The type of the output document data.
 * @param {string} collectionName - The name of the Firestore collection.
 * @param {(data: TInput) => TOutput | Promise<TOutput>} [transformData] - Optional function to transform the data before submission.
 * @returns {Mutation} A mutation object from react-query.
 *
 * @example
 * const writeDocument = useWriteDocument('collectionName');
 * writeDocument.mutate({ data: documentData });
 */
export const useWriteDocument = <TInput extends object, TOutput extends object>(
  collectionName: string,
  transformData?: (data: TInput) => TOutput | Promise<TOutput>
) => {
  return useMutation<string, Error, { data: TInput }>({
    mutationFn: async ({ data }) => {
      const dataToSubmit = transformData ? await transformData(data) : data
      return await writeDocument(dataToSubmit, collectionName)
    },
  })
}

/**
 * Custom hook to listen to a Firestore query in real-time and update the cache.
 *
 * @template T - The type of the document data.
 * @param {QueryKey} queryKey - The query key for react-query.
 * @param {string} collectionName - The name of the Firestore collection.
 * @param {Array<QueryConstraint>} [constraints] - Optional filters for the Firestore query.
 * @param {(oldData: T[], incomingDocuments: T[]) => T[]} [cacheUpdater] - Optional function to update the cache.
 *
 * @example
 * useListenToFirestoreQuery('[bookings, pending]', 'tempBookings', [{ field: 'status', operator: '==', value: 'pending' }]);
 */
export const useListenToFirestoreQuery = <T extends FirestoreData>(
  queryKey: QueryKey,
  collectionName: string,
  constraints?: QueryConstraint[],
  cacheUpdater?: (oldData: T[], incomingDocuments: T[]) => T[]
) => {
  const queryClient = useQueryClient()
  const skipNextRealTimeAtom = createSkipNextRealtimeAtom(collectionName)
  const [skipNextRealTime, setSkipNextRealTime] = useAtom(skipNextRealTimeAtom)
  const skipNextRealTimeRef = useRef(skipNextRealTime)
  const cacheUpdaterRef = useRef(cacheUpdater)

  useEffect(() => {
    skipNextRealTimeRef.current = skipNextRealTime
  }, [skipNextRealTime])

  useEffect(() => {
    cacheUpdaterRef.current = cacheUpdater
  }, [cacheUpdater])

  const queryKeySerialized = JSON.stringify(queryKey)
  useEffect(() => {
    let isActive = true
    let unsubscribe: (() => void) | undefined

    void (async () => {
      const q = await createFirestoreQuery(collectionName, constraints)
      if (!isActive) {
        return
      }

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (!isActive) {
            return
          }

          if (skipNextRealTimeRef.current) {
            setSkipNextRealTime(false)
            return
          }

          const incomingDocuments = mapSnapshotToDocuments<T>(snapshot)
          const updater = cacheUpdaterRef.current

          queryClient.setQueryData(queryKey, updater ? (oldData: T[] = []) => updater(oldData, incomingDocuments) : incomingDocuments)
        },
        (error) => console.error(`Error listening to documents (${collectionName}):`, error)
      )
    })()

    return () => {
      isActive = false
      unsubscribe?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- constraints omitted (callers pass inline arrays); cacheUpdater read via ref; setSkipNextRealTime is a stable Jotai setter
  }, [queryClient, queryKeySerialized, collectionName])
}

/**
 * Custom hook to fetch data from a Firestore collection and update the cache.
 *
 * @template T - The type of the document data.
 * @param {QueryKey} queryKey - The query key for react-query.
 * @param {string} collectionName - The name of the Firestore collection.
 * @param {Array<QueryConstraint>} [constraints] - Optional filters for the Firestore query.
 * @returns {Query} A query object from react-query.
 *
 * @example
 * const { data, isLoading, error } = useFirestoreQuery('[bookings, pending]', 'tempBookings', [{ field: 'status', operator: '==', value: 'pending' }]);
 */
export const useFirestoreQuery = <T extends FirestoreData>(
  queryKey: QueryKey,
  collectionName: string,
  constraints?: QueryConstraint[],
  selectedFields?: string[],
  options?: { enabled?: boolean; staleTime?: number; refetchOnMount?: boolean | 'always'; refetchOnWindowFocus?: boolean }
) => {
  return useQuery<T[]>({
    queryKey: queryKey,
    enabled: options?.enabled,
    staleTime: options?.staleTime,
    refetchOnMount: options?.refetchOnMount,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
    queryFn: async () => {
      const q = await createFirestoreQuery(collectionName, constraints, selectedFields)
      const snapshot = await getDocs(q)
      return mapSnapshotToDocuments<T>(snapshot)
    },
  })
}

/** Custom hook to fetch a single document from a Firestore collection and update the cache.
 *
 * @param queryKey
 * @param collectionName
 * @param docId
 * @param selectedFields
 */

export const useFirestoreDoc = <T extends FirestoreData>(queryKey: QueryKey, collectionName: string, docId: string) => {
  return useQuery<T>({
    queryKey: queryKey,
    queryFn: async () => {
      const db = await getDb()
      const docRef = doc(db, collectionName, docId)
      const docSnapshot = await getDoc(docRef)

      if (!docSnapshot.exists()) {
        throw new Error('Document not found')
      }

      const data = docSnapshot.data()

      return data as T
    },
  })
}

/**
 * Custom hook to update a document in a Firestore collection and update the cache.
 *
 * @template T - The type of the document data.
 * @param {string} collectionName - The name of the Firestore collection.
 * @param {(data: T) => object} firebaseUpdater - Function to update the document data.
 * @param {QueryKey} removeQueryKey - The query key for the old cache.
 * @param {QueryKey} addQueryKey - The query key for the new cache.
 * @param {Updater<T>} cacheAddUpdater - Function to update the cache.
 * @returns {Function} A function to update the document and cache.
 *
 * @example
 * const updateDocument = useUpdateDocument('collectionName', (data) => ({ status: 'approved' }), '[key, pending]', '[key, approved]', (oldData, document) => [...oldData, document]);
 * updateDocument(documentId);
 */
export const useUpdateDocument = <T extends FirestoreData>(
  collectionName: string,
  firebaseUpdater: (data: T) => object,
  removeQueryKey: QueryKey,
  addQueryKey: QueryKey,
  cacheAddUpdater: Updater<T>
) => {
  const skipNextRealTimeAtom = createSkipNextRealtimeAtom(collectionName)
  const [, setSkipNextRealTime] = useAtom(skipNextRealTimeAtom)
  const queryClient = useQueryClient()

  const updateDocumentAndCache = async (documentId: string) => {
    // skip the next real-time update from firestore listener
    setSkipNextRealTime(true)
    const fullData = queryClient.getQueryData<T[]>(removeQueryKey)?.find((doc) => doc.id === documentId)
    if (!fullData) {
      return
    }
    // update the document in firestore
    const data = firebaseUpdater(fullData)
    await updateDocument(documentId, data, collectionName)
    // update the cache
    // remove the document from the old query cache
    queryClient.setQueryData(removeQueryKey, (oldData: T[]) => removeDataUpdater(oldData, fullData))
    // add the document to the new query cache
    queryClient.setQueryData(addQueryKey, (oldData: T[]) => cacheAddUpdater(oldData, fullData))
  }

  return updateDocumentAndCache
}
